import { Dashboard } from '../components/Dashboard/model'
import { isUndefined } from 'util'
import {
  AlertsMetaResponse,
  API,
  ApplicationId,
  ARMServer,
  AssetResponse,
  DetailedAPI,
  LogPointsConnectors,
  PutSettingsApplication,
  SettingsSimpleApplication
} from './models'
import { Date } from '../components/RawData/model'
import { Profile } from '../lifecycle/auth'
import RemoteApi, { HttpMethod, StringMap } from './BaseRemoteApi'
import Option from '../util/Option'
import visualizerService from '../services/VisualizerService'
import { builtInTimezone } from '../components/BuiltInDashboard/model'
import { ReportsApplication } from '../components/Reports/model'
import { Alert, AlertsFeatureFlags, BasicAlert } from '../components/Alerts/model'
import { AlertsFilters } from '../components/Alerts/AlertsListPage/model'
import { isAllFilter } from '../components/Alerts/AlertsListPage/AlertsListPageFilters/filters'

export default class UIRemoteApi extends RemoteApi {

  private readonly maybeProfile: Option<Profile>

  private readonly metricsApiBaseUri = 'metrics'

  constructor(authorization: string, maybeProfile: Option<Profile>) {
    super('/monitoring/api', authorization)

    this.maybeProfile = maybeProfile
  }

  sync(): Promise<void> {
    // this should probably be a post
    const cached = this.initialCache('sync')
    return cached || this._get(['sync'], true)
  }

  search(query?: string): Promise<Dashboard[]> {
    return this._get(
      ['visualizer', 'api', 'search'],
      true,
      this._headersCache(),
      {query: isUndefined(query) ? '' : query}
    )
  }

  preload(): Promise<string> {
    // returns any page to do preload
    return this._get(
      ['visualizer', 'dashboard', 'new'],
      false,
      {},
      {token: this.authorization}
    )
  }

  fetchApplication(envId: string): Promise<ApplicationId[]> {
    return this._get(['visualizer', 'environments', envId, 'applications'], true)
  }

  fetchAMServers(orgId: string, envId: string): Promise<ARMServer[]> {
    return this._get(['organizations', orgId, 'environments', envId, 'servers'], true)
  }

  fetchAlertsFeatureFlags(orgId: string): Promise<AlertsFeatureFlags> {
    return this._get(['v2', 'organizations', orgId, 'alerts', 'enabled'], true)
  }

  fetchBasicAlertsLimitExceeded(orgId: string): Promise<AlertsFeatureFlags> {
    return this._get(['v2', 'organizations', orgId, 'alerts', 'limit', 'exceeded'], true)
  }

  fetchAlertsMeta(orgId: string): Promise<AlertsMetaResponse> {
    return this._get(['v2', 'organizations', orgId, 'alerts', 'meta'], true)
  }

  fetchSettingsApplication(orgId: string, envId: string): Promise<SettingsSimpleApplication[]> {
    return this._get(['v2', 'settings', 'cloudhub', 'organizations', orgId, 'environments', envId, 'applications'], true)
  }

  fetchAllAPIs(orgId: string, envId: string, httpMetricsCompatible?: boolean): Promise<AssetResponse> {
    const queryParams = {
      'http_metrics': httpMetricsCompatible || false
    }
    return this._get(['organizations', orgId, 'environments', envId, 'apis'], true, undefined, queryParams)
  }

  fetchApi(orgId: string, envId: string, apiId: string): Promise<API> {
    return this._get(['organizations', orgId, 'environments', envId, 'apis', apiId], true, undefined)
  }

  fetchDetailedApis(orgId: string, apiIds: number[], includePolicies: boolean): Promise<DetailedAPI[]> {
    const body = {
      apiIds: apiIds,
      includePolicies: includePolicies
    }

    return this._post(['organizations', orgId, 'apis'], body, true)
  }

  saveBasicAlert(orgId: string, alert: BasicAlert): Promise<BasicAlert> {
    if (alert.alertId) {
      delete alert.masterOrganizationId
      delete alert.organizationId
      delete alert.newStateDate
      delete alert.alertState
      delete alert.evalData

      return this._post(
        ['v2', 'organizations', orgId, 'alerts', alert.alertId],
        alert,
        true
      )
    } else {
      return this._post(
        ['v2', 'organizations', orgId, 'alerts'],
        alert,
        true
      )
    }
  }

  deleteBasicAlert(orgId: string, alert: BasicAlert): Promise<void> {
    if (alert.alertId) {
      return this._delete(
        ['v2', 'organizations', orgId, 'alerts', alert.alertId],
        {},
        false
      )
    } else {
      return Promise.reject(alert)
    }
  }

  fetchAlerts(orgId: string, filters: AlertsFilters & {search?: string}, url?: string): Promise<{headers: Headers, dataPromise: Promise<Alert[]>}> {
    const queryParams: any = {
      sortBy: 'updated',
      order: 'descending',
    }

    if (filters.search) queryParams.name = filters.search
    if (!isAllFilter(filters.state)) queryParams.state = filters.state
    if (!isAllFilter(filters.severity)) queryParams.severity = filters.severity

    if (filters.type) {
      queryParams.type = filters.type === 'advanced' ? 'complex' : filters.type
    }

    if (filters.resource === 'panel') queryParams.type = 'complex'
    else if (!isAllFilter(filters.resource)) queryParams.resource = filters.resource

    if (url) {
      return this._getWithURL(url, true, {}, queryParams, true)
    } else {
      return this._get(['v2', 'organizations', orgId, 'alerts'], true, {}, queryParams, true)
    }
  }

  fetchAlert(orgId: string, alertId: string): Promise<any> {
    return this._get(['v2', 'organizations', orgId, 'alerts', alertId], true, {}, undefined, true)
  }

  pauseAlert(orgId: string, alertId: string): Promise<void> {
    return this._patch(['v2', 'organizations', orgId, 'alerts', alertId, 'pause'], undefined, false)
  }

  fetchLogFiles(envId: string, appId: string, date: Date) {
    return this._get(['logs', 'files'], true, {}, {environment: envId, app: appId, year: date.year, month: date.month, day: date.day})
  }

  fetchApplicationsData(businessGroup: string, environment: string, selectedType: string, dateRange: any) {
    return this._get(
      [
        'organizations', businessGroup,
        'environments', environment,
        'reports', selectedType,
        'from', dateRange.from,
        'to', dateRange.to
      ]
    )
  }

  fetchApplicationsForReports(businessGroup: string, environment: string): Promise<ReportsApplication[]> {
    return this._get(
      [
        'organizations', businessGroup,
        'environments', environment,
        'applications'
      ]
    )
  }

  fetchAgentInstallerUrl(): Promise<{ url: string }> {
    return this._get(['v2', 'settings', 'agent', 'installer'], true, {})
  }

  fetchCustomMetrics(orgId: string, envId: string, appId: string, from?: string, to?: string) {
    let queryParams: any = {}
    if (from) {
      queryParams.from = encodeURIComponent(visualizerService.parseTimepickerTimeToVisualizerTime(
        visualizerService.fullyDecodeURI(from), false, builtInTimezone))
    }
    if (to) {
      queryParams.to = encodeURIComponent(visualizerService.parseTimepickerTimeToVisualizerTime(
        visualizerService.fullyDecodeURI(to), true, builtInTimezone))
    }

    return this._get([this.metricsApiBaseUri,
      'organizations', orgId,
      'environments', envId,
      'applications', appId, 'custom'
    ],               true, {}, queryParams)
  }

  fetchCustomMetricFieldKeys(orgId: string, envId: string, appId: string, metric: string) {
    return this._get([this.metricsApiBaseUri,
      'organizations', orgId,
      'environments', envId,
      'applications', appId,
      'custom', metric, 'fields'
    ],               true)
  }

  fetchCustomMetricDimensions(orgId: string, envId: string, appId: string, metric: string, from?: string, to?: string) {
    const queryParams: any = {}
    if (from) {
      queryParams.from = encodeURIComponent(visualizerService.parseTimepickerTimeToVisualizerTime(from, false, builtInTimezone))
    }
    if (to) {
      queryParams.to = encodeURIComponent(visualizerService.parseTimepickerTimeToVisualizerTime(to, true, builtInTimezone))
    }

    return this._get([this.metricsApiBaseUri,
      'organizations', orgId,
      'environments', envId,
      'applications', appId,
      'custom', metric, 'dimensions'
    ],               true, {}, queryParams)
  }

  fetchLabels(orgId: string) {
    return this._get(['organizations', orgId, 'labels'], true, {})
  }

  fetchEnvironments(orgId: string) {
    return this._get(['v2', 'settings', 'cloudhub', 'organizations', orgId, 'environments'], true, {})
  }

  fetchCompatibility(orgId: string, envId: string, resType: string, resId: string) {
    const cacheHeaders = {'Cache-Control': 'max-age=180'}
    return this._get(
      [this.metricsApiBaseUri, 'organizations', orgId, 'environments', envId, 'resources', resType, resId, 'compatibility'], true, cacheHeaders
    )
  }

  cloudHubSettings(method: HttpMethod= 'GET', orgId: string, enable?: boolean) {
    const pathElements = [ 'v2', 'settings', 'cloudhub', 'organizations', orgId , 'settings']
    if (method === 'GET') {
      return this._get(pathElements, true)
    } else {
      const body = this.buildBodyCloudHubSettings(orgId, enable)
      return this._post( pathElements, body, true)
    }
  }

  fetchLogPointsConnectors(): Promise<LogPointsConnectors> {
    return this._get(['log-points', 'connectors'], true)
  }

  fetchTargetsFromInflux(orgId: string, envId: string, appId: string, from: string, to: string, isHybrid: boolean) {
    const queryParams = {
        from:from,
        to:to,
        isHybrid:isHybrid
     }

  return this._get(['organizations', orgId, 'environments', envId,'application',appId, 'targets'], true, {}, queryParams)
  }

  putSettingsApplication(orgId: string, envId: string, app: PutSettingsApplication) {
    const path = ['v2', 'settings', 'cloudhub', 'organizations', orgId, 'environments', envId]
    const suffixPath = ['applications', app.applicationName]
    return this._put(path.concat(suffixPath), app, true, {})
  }

  _headersCache(): StringMap {
    return {
      ...this._headers(),
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  }

  _authorizationHeader(): StringMap {
    const baseAuthorizationHeaders = super._authorizationHeader()
    const headers = this.uiApiHeaders()
    return {...baseAuthorizationHeaders, ...headers}
  }

  private uiApiHeaders(): StringMap {
    return this.maybeProfile
      .map<string>(profile => profile.organizationId)
      .map<StringMap>(activeOrgId => ({'X-ACTIVE-ORG-ID': activeOrgId}))
      .getOrElse(({}))
  }

  private buildBodyCloudHubSettings(orgId: string, enable?: boolean) {
    return {
      organizationId: orgId,
      settings: {
        deployment: {
          newAppsAgentEnabled: enable
        }
      }
    }
  }

}
